/**
 * <brand-video> — an ambient background video.
 *
 * Autoplays muted and looping with no visible controls, and pauses while the pointer is
 * over it. Pausing on hover is deliberate: the video is decoration, and it should get out
 * of the way the moment someone engages with the section.
 *
 * Accessibility: `prefers-reduced-motion` stops it autoplaying at all, and a pause control
 * exists in the DOM for keyboard users — hidden until focused, so it never shows visually
 * while still satisfying the requirement that auto-playing media can be stopped.
 */
class BrandVideo extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');

    if (!this.video) return;

    const { signal } = this.#abortController;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Paused by the pointer, as opposed to paused by the user — tracked separately so a
    // deliberate pause is not undone when the cursor leaves.
    this.userPaused = this.reduceMotion.matches;

    if (this.reduceMotion.matches) {
      this.video.pause();
    } else {
      this.#play();
    }

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.toggle?.addEventListener('click', this.#onToggle, { signal });
    this.reduceMotion.addEventListener('change', this.#onMotionChange, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  #play() {
    // Autoplay can still be refused by the browser; ignore the rejection rather than throw.
    this.video?.play?.().catch(() => {});
  }

  #onPointerEnter = () => {
    if (this.userPaused) return;
    this.video.pause();
  };

  #onPointerLeave = () => {
    if (this.userPaused) return;
    this.#play();
  };

  #onToggle = () => {
    this.userPaused = !this.userPaused;

    if (this.userPaused) {
      this.video.pause();
    } else {
      this.#play();
    }

    if (this.toggle) {
      this.toggle.textContent = this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
    }
  };

  #onMotionChange = (event) => {
    this.userPaused = event.matches;
    if (event.matches) this.video.pause();
    else this.#play();
  };
}

if (!customElements.get('brand-video')) {
  customElements.define('brand-video', BrandVideo);
}
